import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import Sidebar from "../Sidebar/Sidebar";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Stack,
  Autocomplete,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import { createFilterOptions } from "@mui/material/Autocomplete";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import { purple } from "@mui/material/colors";
import { styled } from "@mui/material/styles";

toast.configure();

const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(purple[500]),
  backgroundColor: purple[500],
  boxShadow: "2px 2px 6px  #404142",
  "&:hover": {
    backgroundColor: purple[700],
  },
}));

const VIEW_MODE = {
  PICKLIST: "PICKLIST",
  BOX: "BOX",
};

const CHIP_FILTER = {
  ALL: "ALL",
  SCAN_ZERO: "SCAN_ZERO",
  QTY_MISMATCH: "QTY_MISMATCH",
  MRP_MISMATCH: "MRP_MISMATCH",
  SAME_ITEM_DIFF_BOX: "SAME_ITEM_DIFF_BOX",
};

const n0 = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

const EmodeBoxno = () => {
  const userToken = LocalstorageHelper.GetUserToken();

  const redirectToLoginPage = () => {
    if (!userToken) {
      return <Redirect exact to="/sparepartsLogin" />;
    }
  };

  // SET SEARCHFIELD'S CONFIGURATION
  const OPTIONS_LIMIT = 15;
  const filterOptions = createFilterOptions({
    limit: OPTIONS_LIMIT,
    trim: true,
  });

  const [disableUpdate, setDisableUpdate] = useState(false);
  const [partyinput, setpartyinput] = useState(null);
  const [partyopts, setpartyopts] = useState([]);

  const [orderinput, setorderinput] = useState(null);
  const [orderopts, setorderopts] = useState([]);

  const [rows, setrows] = useState([]);
  const [pdfStream, setPdfStream] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chipVisible, setChipVisible] = useState(false);

  // ✅ default view
  const [viewMode, setViewMode] = useState(VIEW_MODE.PICKLIST);

  // ✅ chip filter state
  const [chipFilter, setChipFilter] = useState(CHIP_FILTER.ALL);

  // ✅ UX friendly toggle: Show billed ON/OFF (OFF = only unbilled)
  const [showBilled, setShowBilled] = useState(false);

  // ------------------------- SAME ITEM / DIFF BOX COLOR LOGIC (BOX VIEW ONLY) -------------------------
  const partnoBoxInfo = useMemo(() => {
    const map = new Map();
    for (const r of rows || []) {
      const key = String(r.partno ?? "").trim();
      if (!key) continue;
      const b = String(r.boxno ?? "").trim();
      if (!map.has(key)) map.set(key, new Set());
      if (b !== "") map.get(key).add(b);
    }
    return map;
  }, [rows]);

  const hasSameItemDiffBox = useCallback(
    (row) => {
      const key = String(row?.partno ?? "").trim();
      const set = partnoBoxInfo.get(key);
      return !!(set && set.size > 1);
    },
    [partnoBoxInfo],
  );

  const boxBucket = (boxno) => {
    const n = parseInt(String(boxno ?? "0"), 10);
    return Number.isFinite(n) ? Math.abs(n) % 6 : 0;
  };

  // ------------------------- PICKLIST-WISE AGGREGATION -------------------------
  const pickListRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const map = new Map();

    rows.forEach((r) => {
      const key = String(r.partno ?? "").trim();
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          ...r,
          boxno: null,
          __ordqty_sum: 0,
          __ordqty_count: 0,

          // sums
          stkqty: 0,
          scannedqty: 0,
          losqty: 0,
          saleqty: 0,
          value: 0,
          disamt: 0,
          taxable: 0,
          gstamt: 0,
          netamt: 0,

          __mrp_mismatch: false,
        });
      }

      const t = map.get(key);

      // AVG ordqty
      t.__ordqty_sum += n0(r.ordqty);
      t.__ordqty_count += 1;

      // SUM others
      t.stkqty += n0(r.stkqty);
      t.scannedqty += n0(r.scannedqty);
      t.losqty += n0(r.losqty);
      t.saleqty += n0(r.saleqty);
      t.value += n0(r.value);
      t.disamt += n0(r.disamt);
      t.taxable += n0(r.taxable);
      t.gstamt += n0(r.gstamt);
      t.netamt += n0(r.netamt);

      // mrp mismatch flag
      if (Number(r?.ordmrp) !== Number(r?.mrp)) t.__mrp_mismatch = true;
    });

    return Array.from(map.values()).map((r, idx) => {
      const avgOrd =
        r.__ordqty_count > 0 ? r.__ordqty_sum / r.__ordqty_count : 0;

      const out = {
        ...r,
        serial: idx + 1,
        ordqty: Number(avgOrd.toFixed(2)),
        __mrp_mismatch: !!r.__mrp_mismatch,
      };
      delete out.__ordqty_sum;
      delete out.__ordqty_count;
      return out;
    });
  }, [rows]);

  // ------------------------- MISMATCH HELPERS (WORKS BOTH VIEWS) -------------------------
  // const isQtyMismatchRow = useCallback(
  //   (r) => n0(r?.ordqty) !== n0(r?.scannedqty),
  //   []
  // );
  const isQtyMismatchRow = useCallback((r) => {
    const ord = n0(r?.ordqty);
    const scan = n0(r?.scannedqty);
    return scan > 0 && ord !== scan; // ✅ scan zero excluded
  }, []);
  const isScanZeroRow = useCallback((r) => n0(r?.scannedqty) === 0, []);

  const scanCmp = useCallback((r) => {
    const ord = n0(r?.ordqty);
    const scan = n0(r?.scannedqty);
    if (scan === 0) return "ZERO";
    if (scan < ord) return "LESS";
    if (scan > ord) return "MORE";
    return "OK";
  }, []);

  const isMrpMismatchRow = useCallback(
    (r) => {
      if (viewMode === VIEW_MODE.PICKLIST) return !!r?.__mrp_mismatch;
      return Number(r?.ordmrp) !== Number(r?.mrp);
    },
    [viewMode],
  );

  // ------------------------- COLUMNS -------------------------
  const columns = [
    {
      field: "serial",
      headerClassName: "super-app-theme--header",
      headerName: "Srl",
      type: "number",
      textAlign: "left",
      width: 30,
    },
    {
      field: "partno",
      headerClassName: "super-app-theme--header",
      headerName: "Partno",
      cellClassName: "custom-font-size",
      width: 350,
      renderCell: (params) => {
        const raw = String(params.value ?? "");
        const idx = raw.indexOf("---");
        if (idx === -1) {
          return <div style={{ whiteSpace: "pre-wrap" }}>{raw}</div>;
        }
        const code = raw.slice(0, idx).trim();
        const rest = raw.slice(idx + 3).trim();

        return (
          <div style={{ whiteSpace: "pre-wrap" }}>
            <span className="partno-code">{code}</span>
            <span className="partno-sep"> --- </span>
            <span>{rest}</span>
          </div>
        );
      },
    },
    {
      field: "stkqty",
      headerClassName: "super-app-theme--header",
      headerName: "StkQty",
      width: 80,
      type: "number",
    },
    {
      field: "rackname",
      headerClassName: "super-app-theme--header",
      headerName: "Rack",
      width: 70,
      type: "number",
    },
    {
      field: "ordqty",
      headerClassName: "super-app-theme--header",
      headerName: "OrdQty",
      width: 80,
      type: "number",
      cellClassName: "hl-qty-cell ordqty-cell",
    },

    // ✅ BoxNo (BOX view only)
    {
      field: "boxno",
      headerClassName: "super-app-theme--header",
      headerName: "BoxNo",
      width: 80,
      type: "number",
      editable: true,
      cellClassName: (params) => {
        if (viewMode !== VIEW_MODE.BOX) return "";
        if (!hasSameItemDiffBox(params.row)) return "";
        return `diff-box-cell box-bucket-${boxBucket(params.value)}`;
      },
      // ✅ immutable update
      valueSetter: (params) => {
        const boxno = params.value;

        setrows((prev) =>
          (prev || []).map((r) =>
            r.itemid === params.row.itemid ? { ...r, boxno } : r,
          ),
        );

        return { ...params.row, boxno };
      },
    },

    {
      field: "scannedqty",
      headerClassName: "super-app-theme--header",
      headerName: "ScanQty",
      width: 80,
      type: "number",
      cellClassName: (params) => {
        const ord = n0(params.row?.ordqty);
        const scan = n0(params.value);
        let cls = "hl-qty-cell scanqty-cell";
        if (scan === 0) cls += " scan-zero-cell";
        else if (scan < ord) cls += " scan-less-cell";
        else if (scan > ord) cls += " scan-more-cell";
        else cls += " scan-ok-cell";
        return cls;
      },
    },
    {
      field: "losqty",
      headerClassName: "super-app-theme--header",
      headerName: "LosQty",
      width: 80,
      type: "number",
    },
    {
      field: "saleqty",
      headerClassName: "super-app-theme--header",
      headerName: "SaleQty",
      type: "number",
      width: 80,
      editable: false,
    },
    {
      field: "ordmrp",
      headerClassName: "super-app-theme--header",
      headerName: "OrdMRP",
      width: 80,
      type: "number",
    },
    {
      field: "mrp",
      headerClassName: "super-app-theme--header",
      headerName: "MRP",
      width: 80,
      type: "number",
      cellClassName: (params) => {
        if (viewMode === VIEW_MODE.PICKLIST) {
          return params.row?.__mrp_mismatch ? "diff-mrp-cell" : "";
        }
        const ordMrp = Number(params.row.ordmrp);
        const scanMrp = Number(params.value);
        return ordMrp !== scanMrp ? "diff-mrp-cell" : "";
      },
    },
    {
      field: "price",
      headerClassName: "super-app-theme--header",
      headerName: "Price",
      width: 80,
      type: "number",
    },
    {
      field: "value",
      headerClassName: "super-app-theme--header",
      headerName: "Value",
      width: 100,
      type: "number",
    },
    {
      field: "dispers",
      headerClassName: "super-app-theme--header",
      headerName: "Dis%",
      width: 80,
      type: "number",
    },
    {
      field: "disamt",
      headerClassName: "super-app-theme--header",
      headerName: "DisAmt",
      width: 100,
      type: "number",
    },
    {
      field: "taxable",
      headerClassName: "super-app-theme--header",
      headerName: "Taxable",
      width: 80,
      type: "number",
    },
    {
      field: "gstpers",
      headerClassName: "super-app-theme--header",
      headerName: "GST%",
      width: 60,
      type: "number",
    },
    {
      field: "gstamt",
      headerClassName: "super-app-theme--header",
      headerName: "GST Amt",
      width: 120,
      type: "number",
    },
    {
      field: "netamt",
      headerClassName: "super-app-theme--header",
      headerName: "Net Amt",
      width: 120,
      type: "number",
    },
  ];

  // ✅ PickList view: ONLY show required columns
  const pickListColumns = useMemo(() => {
    const needed = [
      "serial",
      "partno",
      "rackname",
      "ordqty",
      "scannedqty",
      "stkqty",
      "mrp",
    ];
    const map = new Map(columns.map((c) => [c.field, c]));
    return needed.map((f) => map.get(f)).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const boxColumns = columns;

  // ------------------------- API CALLS -------------------------
  async function getparty() {
    try {
      const response = await fetch("/corder/scanned/party", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setpartyopts(jsonData || []);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getparty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getorders() {
    try {
      const response = await fetch(
        `/corder/scanned/orders/${partyinput.subledgerid}`,
        {
          method: "GET",
          headers: { sptoken: userToken },
        },
      );
      const jsonData = await response.json();
      setorderopts(jsonData || []); // ✅ raw
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    if (partyinput) getorders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyinput]);

  // ✅ frontend billed flag based on billheaderid from fetch
  const orderoptsWithStatus = useMemo(() => {
    return (orderopts || []).map((o) => {
      const bh = o?.billheaderid;
      const billed = bh !== null && bh !== undefined && Number(bh) !== 0;

      return {
        ...o,
        billheaderid: bh ?? null,
        billed,
        unbilled: !billed,
      };
    });
  }, [orderopts]);

  // ✅ filter orders for dropdown (OFF = only unbilled, ON = all)
  const visibleOrderOpts = useMemo(() => {
    if (showBilled) return orderoptsWithStatus;
    return orderoptsWithStatus.filter((o) => o.unbilled);
  }, [orderoptsWithStatus, showBilled]);

  // ✅ if showBilled OFF and currently selected order is billed, clear it
  useEffect(() => {
    if (showBilled) return;
    if (!orderinput) return;

    const bh = orderinput?.billheaderid;
    const isBilled = bh !== null && bh !== undefined && Number(bh) !== 0;

    if (isBilled) {
      setorderinput(null);
      setrows([]);
      setPdfStream(null);
      setChipVisible(false);
      setChipFilter(CHIP_FILTER.ALL);
      toast.error("Billed order is hidden because 'Show billed' is OFF.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBilled, orderinput]);

  const getorderdata = useCallback(async () => {
    try {
      const response = await fetch(
        `/corder/scanned/orderdata/${orderinput.corderheaderid}`,
        {
          method: "GET",
          headers: { sptoken: userToken },
        },
      );
      const jsonData = await response.json();
      setrows(jsonData || []);
      setChipVisible(true);
      setChipFilter(CHIP_FILTER.ALL);
    } catch (err) {
      console.error(err.message);
    }
  }, [orderinput, userToken]);

  useEffect(() => {
    if (orderinput) getorderdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderinput]);

  // ✅ Refresh button handler
  const handleRefresh = async () => {
    try {
      if (!orderinput) {
        toast.error("Select an Order to refresh");
        return;
      }
      setLoading(true);
      setPdfStream(null);
      await getorderdata();
      toast.success("Refreshed!");
    } catch (e) {
      console.log(e?.message);
    } finally {
      setLoading(false);
    }
  };

  // ------------------------- UPDATE (BOX VIEW EDIT MODE) -------------------------
  const handleEditmodeSubmit = async (e) => {
    e.preventDefault();

    if (viewMode !== VIEW_MODE.BOX) {
      toast.error("Switch to Box-wise view to update BoxNo.");
      return;
    }

    if (!orderinput) {
      toast.error("Select an Order");
      return;
    }

    // ✅ block update if selected order is billed (billheaderid exists)
    const bh = orderinput?.billheaderid;
    const orderIsBilled = bh !== null && bh !== undefined && Number(bh) !== 0;

    if (orderIsBilled) {
      toast.error("This order is already billed. Edit not allowed.");
      return;
    }

    const hasAlreadyBilledRow = rows.some(
      (row) =>
        row.salebillid !== null &&
        row.salebillid !== undefined &&
        row.salebillid !== 0,
    );

    if (hasAlreadyBilledRow) {
      setDisableUpdate(false);
      toast.error(
        "Some items in this order are already billed. Edit not allowed.",
      );
      return;
    }

    try {
      setDisableUpdate(true);
      const body = { rows, headerid: orderinput.corderheaderid };

      const response = await fetch("/corder/scanned/up", {
        method: "POST",
        headers: { "Content-Type": "application/json", sptoken: userToken },
        body: JSON.stringify(body),
      });

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message);
        setTimeout(() => window.location.reload(false), 3000);
      } else {
        toast.error(parseRes.message);
        setDisableUpdate(false);
      }
    } catch (err) {
      console.log(err.message);
      setDisableUpdate(false);
    }
  };

  // ------------------------- REPORT -------------------------
  const handleReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (orderinput === null) {
        setLoading(false);
        toast.error("Please Select A valid Order!");
      } else {
        const body = { orderid: orderinput.corderheaderid };
        const options = {
          headers: { "Content-Type": "application/json", sptoken: userToken },
          method: "POST",
          body: JSON.stringify(body),
        };

        await fetch("/reports/corder/scannedcopy", options)
          .then((response) => response.blob())
          .then((pdfBlob) => {
            const pdfUrl = URL.createObjectURL(pdfBlob);
            setPdfStream(pdfUrl);
          })
          .catch((error) => console.error(error));

        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      console.log(err.message);
    }
  };

  const myElementRef = useRef(null);

  useEffect(() => {
    if (myElementRef.current) {
      myElementRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [pdfStream]);

  // ------------------------- EXCEL -------------------------
  const printexcel = async (e) => {
    e.preventDefault();
    try {
      if (orderinput === null) {
        toast.error("Select A Order");
        return;
      }
      const body = { orderid: orderinput.corderheaderid };
      const data = await fetch("/reports/corder/scannedcopy/excel", {
        method: "POST",
        headers: { sptoken: userToken, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const blob = await data.blob();

      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = "ScannedCopy.xlsx";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.log(error.message);
    }
  };

  // ------------------------- CHIP FILTER APPLY (BOTH VIEWS) -------------------------
  const baseRows = viewMode === VIEW_MODE.PICKLIST ? pickListRows : rows;

  const filteredRows = useMemo(() => {
    if (chipFilter === CHIP_FILTER.ALL) return baseRows;

    if (chipFilter === CHIP_FILTER.SCAN_ZERO) {
      return baseRows.filter((r) => isScanZeroRow(r));
    }

    if (chipFilter === CHIP_FILTER.QTY_MISMATCH) {
      return baseRows.filter((r) => isQtyMismatchRow(r));
    }

    if (chipFilter === CHIP_FILTER.MRP_MISMATCH) {
      return baseRows.filter((r) => isMrpMismatchRow(r));
    }

    if (chipFilter === CHIP_FILTER.SAME_ITEM_DIFF_BOX) {
      return baseRows.filter((r) => hasSameItemDiffBox(r));
    }

    return baseRows;
  }, [
    baseRows,
    chipFilter,
    hasSameItemDiffBox,
    isQtyMismatchRow,
    isMrpMismatchRow,
    isScanZeroRow,
  ]);

  const gridRows = filteredRows;

  const gridColumns =
    viewMode === VIEW_MODE.PICKLIST ? pickListColumns : boxColumns;

  const chipBtnSx = (active, bg, border, textColor) => ({
    backgroundColor: bg,
    color: textColor,
    fontWeight: 800,
    border: active ? `2px solid ${border}` : `1px solid ${border}`,
    boxShadow: active ? `0 0 0 3px rgba(0,0,0,0.12)` : "none",
    transform: active ? "scale(1.03)" : "none",
    cursor: "pointer",
  });

  return (
    <div>
      <Sidebar
        Title={"EDIT MODE FOR BOXNO."}
        expandedPanel={"panel2"}
        children={
          <Box component="form" sx={{ width: "100%" }} autoComplete="off">
            <Stack
              spacing={1}
              mb={"10px"}
              direction="row"
              justifyContent={"center"}
              alignItems="center"
            >
              <Autocomplete
                id="partyno"
                value={partyinput}
                filterOptions={filterOptions}
                onChange={(event, newValue) => {
                  setpartyinput(newValue);
                  setorderinput(null);
                  setorderopts([]);
                  setrows([]);
                  setPdfStream(null);
                  setChipVisible(false);
                  setChipFilter(CHIP_FILTER.ALL);
                }}
                options={partyopts}
                isOptionEqualToValue={(opt, val) =>
                  opt?.subledgerid === val?.subledgerid
                }
                getOptionLabel={(option) => option?.partydetails || ""}
                sx={{ width: 420 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search Party"
                  />
                )}
              />

              <Autocomplete
                id="orderno"
                value={orderinput}
                filterOptions={filterOptions}
                onChange={(event, newValue) => {
                  setorderinput(newValue);
                  setPdfStream(null);
                  setChipFilter(CHIP_FILTER.ALL);
                }}
                options={visibleOrderOpts}
                isOptionEqualToValue={(opt, val) =>
                  opt?.corderheaderid === val?.corderheaderid
                }
                getOptionLabel={(option) => {
                  if (!option) return "";
                  if (showBilled && option.billed)
                    return `${option.orderno} (BILLED)`;
                  return option.orderno || "";
                }}
                sx={{ width: 420 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search Order"
                  />
                )}
              />

              <FormControlLabel
                sx={{
                  ml: 1,
                  userSelect: "none",
                  "& .MuiFormControlLabel-label": { fontWeight: 800 },
                }}
                control={
                  <Switch
                    size="small"
                    checked={showBilled}
                    onChange={(e) => setShowBilled(e.target.checked)}
                  />
                }
                label="Show billed"
              />

              <Button
                type="submit"
                onClick={handleReport}
                variant="contained"
                disabled={loading === true}
                component="label"
                size="small"
                sx={{ boxShadow: "2px 2px 6px  #404142" }}
              >
                {loading ? "Fetching Data..." : " Show Report"}
              </Button>

              <Button
                onClick={printexcel}
                size="small"
                variant="contained"
                color="success"
              >
                Excel
              </Button>

              <Button
                onClick={handleRefresh}
                size="small"
                variant="contained"
                color="info"
                disabled={loading || !orderinput}
                sx={{ boxShadow: "2px 2px 6px  #404142" }}
              >
                Refresh
              </Button>
            </Stack>

            {/* ✅ VIEW TOGGLE */}
            <Stack direction="row" spacing={2} justifyContent="center" mb={1}>
              <ColorButton
                size="small"
                variant={
                  viewMode === VIEW_MODE.PICKLIST ? "contained" : "outlined"
                }
                onClick={() => {
                  setViewMode(VIEW_MODE.PICKLIST);
                  setChipFilter(CHIP_FILTER.ALL);
                }}
              >
                PickList Wise
              </ColorButton>

              <ColorButton
                size="small"
                variant={viewMode === VIEW_MODE.BOX ? "contained" : "outlined"}
                onClick={() => {
                  setViewMode(VIEW_MODE.BOX);
                  setChipFilter(CHIP_FILTER.ALL);
                }}
              >
                Box Wise
              </ColorButton>
            </Stack>

            {/* ✅ CHIP FILTERS */}
            <Stack
              display={chipVisible === false ? "none" : "flex"}
              direction="row"
              spacing={2}
              justifyContent="center"
              mb={1}
              flexWrap="wrap"
              gap={1}
            >
              <Chip
                label={chipFilter === CHIP_FILTER.ALL ? "✓ ALL" : "ALL"}
                onClick={() => setChipFilter(CHIP_FILTER.ALL)}
                sx={chipBtnSx(
                  chipFilter === CHIP_FILTER.ALL,
                  "#e0e0e0",
                  "#9e9e9e",
                  "#000",
                )}
              />

              <Chip
                label={
                  chipFilter === CHIP_FILTER.SCAN_ZERO
                    ? "✓ Scanned Qty 0"
                    : "Scanned Qty 0"
                }
                onClick={() => setChipFilter(CHIP_FILTER.SCAN_ZERO)}
                sx={chipBtnSx(
                  chipFilter === CHIP_FILTER.SCAN_ZERO,
                  "#eeeeee",
                  "#616161",
                  "#000",
                )}
              />

              <Chip
                label={
                  chipFilter === CHIP_FILTER.QTY_MISMATCH
                    ? "✓ Qty Mismatch"
                    : "Qty Mismatch"
                }
                onClick={() => setChipFilter(CHIP_FILTER.QTY_MISMATCH)}
                sx={chipBtnSx(
                  chipFilter === CHIP_FILTER.QTY_MISMATCH,
                  "#ffe0b2",
                  "#ff9800",
                  "#000",
                )}
              />

              <Chip
                label={
                  chipFilter === CHIP_FILTER.MRP_MISMATCH
                    ? "✓ MRP Mismatch"
                    : "MRP Mismatch"
                }
                onClick={() => setChipFilter(CHIP_FILTER.MRP_MISMATCH)}
                sx={chipBtnSx(
                  chipFilter === CHIP_FILTER.MRP_MISMATCH,
                  "#ff7043",
                  "#e64a19",
                  "#fff",
                )}
              />

              {viewMode === VIEW_MODE.BOX && (
                <Chip
                  label={
                    chipFilter === CHIP_FILTER.SAME_ITEM_DIFF_BOX
                      ? "✓ Same Item Diff Box"
                      : "Same Item Diff Box"
                  }
                  onClick={() => setChipFilter(CHIP_FILTER.SAME_ITEM_DIFF_BOX)}
                  sx={chipBtnSx(
                    chipFilter === CHIP_FILTER.SAME_ITEM_DIFF_BOX,
                    "#e3f2fd",
                    "#64b5f6",
                    "#000",
                  )}
                />
              )}
            </Stack>

            <Stack
              spacing={1}
              mb={"10px"}
              direction="row"
              justifyContent={"center"}
            >
              <Box
                sx={{
                  height: 550,
                  width: "100%",
                  position: "relative",
                  mt: "10px",
                  boxShadow: "2px 6px 15px #5a5a5a",
                  "& .super-app-theme--header": {
                    backgroundColor: "#3bb9ff",
                    color: "#000",
                  },
                  "& .custom-font-size": {
                    fontSize: 13,
                  },
                }}
              >
                <DataGrid
                  sx={{
                    border: "1px solid #000",
                    "& .MuiDataGrid-cell:focus": {
                      outline: "2px solid red",
                      backgroundColor: "gray",
                      color: "white",
                    },

                    /* ✅ highlight itemcode before --- */
                    "& .partno-code": {
                      fontWeight: 900,
                      backgroundColor: "#fff59d",
                      padding: "1px 6px",
                      borderRadius: "6px",
                      display: "inline-block",
                    },
                    "& .partno-sep": {
                      fontWeight: 900,
                      color: "#424242",
                    },

                    /* ✅ highlight qty cells */
                    "& .hl-qty-cell": {
                      fontWeight: 900,
                    },
                    "& .ordqty-cell": {
                      textDecoration: "underline",
                    },

                    /* ✅ scanned qty text emphasis */
                    "& .scan-zero-cell": { color: "#616161" },
                    "& .scan-less-cell": { color: "#e65100" },
                    "& .scan-more-cell": { color: "#1b5e20" },
                    "& .scan-ok-cell": { color: "#0d47a1" },

                    /* ✅ ROW COLORS based on scanned vs order */
                    "& .MuiDataGrid-row.scan-zero-row": {
                      backgroundColor: "#eeeeee",
                    },
                    "& .MuiDataGrid-row.scan-zero-row:hover": {
                      backgroundColor: "#e0e0e0",
                    },

                    "& .MuiDataGrid-row.scan-less-row": {
                      backgroundColor: "#ffe0b2",
                    },
                    "& .MuiDataGrid-row.scan-less-row:hover": {
                      backgroundColor: "#ffcc80",
                    },

                    "& .MuiDataGrid-row.scan-more-row": {
                      backgroundColor: "#c8e6c9",
                    },
                    "& .MuiDataGrid-row.scan-more-row:hover": {
                      backgroundColor: "#a5d6a7",
                    },

                    // Cell highlight for mrp mismatch
                    "& .diff-mrp-cell": {
                      backgroundColor: "#ff7043",
                      color: "#fff",
                      fontWeight: 700,
                    },

                    // Same item diff box (use border so it doesn't override scan colors)
                    "& .MuiDataGrid-row.same-item-diff-box": {
                      borderLeft: "6px solid #64b5f6",
                    },

                    // Box cell coloring buckets
                    "& .diff-box-cell": {
                      fontWeight: 800,
                      color: "#000",
                      border: "1px solid rgba(0,0,0,0.2)",
                    },
                    "& .diff-box-cell.box-bucket-0": {
                      backgroundColor: "#c8e6c9",
                    },
                    "& .diff-box-cell.box-bucket-1": {
                      backgroundColor: "#ffe0b2",
                    },
                    "& .diff-box-cell.box-bucket-2": {
                      backgroundColor: "#d1c4e9",
                    },
                    "& .diff-box-cell.box-bucket-3": {
                      backgroundColor: "#b2ebf2",
                    },
                    "& .diff-box-cell.box-bucket-4": {
                      backgroundColor: "#ffcdd2",
                    },
                    "& .diff-box-cell.box-bucket-5": {
                      backgroundColor: "#f0f4c3",
                    },
                  }}
                  rowHeight={35}
                  headerHeight={30}
                  showColumnRightBorder={true}
                  rows={gridRows}
                  columns={gridColumns}
                  getRowId={(row) => row.serial}
                  getRowClassName={(params) => {
                    const classes = [];

                    const cmp = scanCmp(params.row);
                    if (cmp === "ZERO") classes.push("scan-zero-row");
                    else if (cmp === "LESS") classes.push("scan-less-row");
                    else if (cmp === "MORE") classes.push("scan-more-row");

                    if (
                      viewMode === VIEW_MODE.BOX &&
                      hasSameItemDiffBox(params.row)
                    ) {
                      classes.push("same-item-diff-box");
                    }

                    return classes.join(" ");
                  }}
                />
              </Box>
            </Stack>

            <Stack
              spacing={1}
              mb={"10px"}
              direction="row"
              justifyContent={"center"}
            >
              <Button
                type="submit"
                onClick={handleEditmodeSubmit}
                disabled={disableUpdate}
                variant="contained"
                color="primary"
                size="large"
                sx={{ boxShadow: "2px 2px 6px  #404142" }}
              >
                UPDATE ORDER
              </Button>
            </Stack>

            <Box ref={myElementRef}></Box>

            <Box ref={myElementRef} sx={{ backgroundColor: "white" }}>
              {pdfStream && (
                <iframe
                  src={pdfStream}
                  width="100%"
                  height="480px"
                  title="report"
                ></iframe>
              )}
            </Box>
          </Box>
        }
      />
      {redirectToLoginPage()}
    </div>
  );
};

export default EmodeBoxno;
